import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, Percent, Gift, ArrowRight } from "lucide-react";
import { SpecialOffer } from "../types";
import { dataService } from "../services/dataService";
import PlayfulLoader from "../components/PlayfulLoader";

export const OffersPage: React.FC = () => {
  const [offers, setOffers] = useState<SpecialOffer[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadOffers = async () => {
      try {
        setIsLoading(true);
        const response = await dataService.getSpecialOffers();
        if (response.success) {
          setOffers(response.data!);
        }
      } catch (error) {
        console.error("Error loading offers:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOffers();
  }, []);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const getDaysUntilExpiry = (date: Date) => {
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (isLoading) {
    return (
      <PlayfulLoader
        size="lg"
        variant="rainbow"
        message="🔥 Loading amazing offers..."
        fullScreen
      />
    );
  }

  return (
    <div className="min-h-screen bg-warm-cream-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-100 via-red-50 to-pink-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-4">🎁</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Special Offers
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Don't miss out on these amazing deals! Limited time offers on your
            favorite toys.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {offers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {offers.map((offer) => {
              const daysLeft = getDaysUntilExpiry(offer.validUntil);
              return (
                <div
                  key={offer.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
                >
                  {/* Offer Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={offer.image}
                      alt={offer.title}
                      className="w-full h-full object-cover"
                    />

                    {/* Discount Badge */}
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold text-lg animate-pulse">
                      {offer.discountPercentage}% OFF
                    </div>

                    {/* Countdown Timer */}
                    {daysLeft > 0 && (
                      <div className="absolute bottom-4 left-4 bg-black bg-opacity-75 text-white px-3 py-2 rounded-lg">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-2" />
                          <span className="text-sm font-medium">
                            {daysLeft} day{daysLeft !== 1 ? "s" : ""} left
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Offer Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {offer.title}
                    </h3>

                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {offer.description}
                    </p>

                    {/* Offer Details */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-green-600">
                        <Percent className="h-5 w-5 mr-2" />
                        <span className="font-semibold">
                          Save up to {offer.discountPercentage}%
                        </span>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <Gift className="h-5 w-5 mr-2" />
                        <span className="text-sm">
                          Valid until {formatDate(offer.validUntil)}
                        </span>
                      </div>
                    </div>

                    {/* Shop Now Button */}
                    <Link
                      to={`/products?category=${offer.categoryIds[0]}`}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                    >
                      <span>Shop Now</span>
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              No offers available right now
            </h3>
            <p className="text-gray-600 mb-4">
              Check back soon for amazing deals and special offers!
            </p>
            <Link
              to="/products"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
            >
              Browse All Products
            </Link>
          </div>
        )}

        {/* Additional Info */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🎉 More Offers Coming Soon!
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Stay tuned for exciting seasonal offers, holiday specials, and
              exclusive deals. Follow us on social media to be the first to know
              about new offers!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/products"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200"
              >
                Browse All Products
              </Link>
              <Link
                to="/contact"
                className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffersPage;
